/**
 * ArticleImageLightbox
 * Shows an Article Image at its full reading size (the server's copy is at
 * most 1600px wide) when a reader clicks it in ArticleBody.
 *
 * Portaled to document.body: rendered in place, the overlay would be a child
 * of the article's layout (HelpArticlePage caps its children at a reading
 * measure) and could not cover the viewport.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import GenericModal from '@components/common/modals/GenericModal';
import styles from './ArticleImageLightbox.module.css';

export interface ArticleImageLightboxImage {
  src: string;
  /** The Image Caption; empty when the image has none. */
  caption: string;
}

export interface ArticleImageLightboxProps {
  image: ArticleImageLightboxImage | null;
  onClose: () => void;
  dataTestId?: string;
}

const ArticleImageLightbox: React.FC<ArticleImageLightboxProps> = ({
  image,
  onClose,
  dataTestId = 'article-image-lightbox'
}) =>
  createPortal(
    <GenericModal
      isOpen={image !== null}
      onClose={onClose}
      title={image?.caption || 'Image'}
      size="lg"
      dataTestId={dataTestId}
      contentClassName={styles.lightboxContent}
      bodyClassName={styles.lightboxBody}
    >
      {image && (
        <img
          src={image.src}
          alt={image.caption}
          className={styles.lightboxImage}
          data-test-id={`${dataTestId}-image`}
        />
      )}
    </GenericModal>,
    document.body
  );

export default ArticleImageLightbox;
